import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import SearchIcon from './search-icon';
import {
  searchBackButton,
  searchFormContainerStyle,
  searchFormTitleStyle,
  searchInputStyle,
  searchNotFoundStyle,
  searchResultContainerStyle,
  searchResultStyle,
  spinnerStyles
} from './search-form.styles';
import { TextInput } from '@atlassiansox/wpl-utils';
import Spinner from '@atlaskit/spinner';
import LeftIcon from '@atlaskit/icon/glyph/chevron-left-large';
import { debounce } from 'lodash';
import { useGlobalStore } from '../../../store';
import { ProductFamilyName, marketplaceAddonDetails } from '../../../models';
import { useSelectedMarketplaceApps } from '../../../selectors';
import { FetchPricingContext, IFetchPricing } from '../../../contexts';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ANALYTICS_EVENTS } from '../../../constants';
import { useModuleProps } from '../../../module-props';
import { extractOfferingSku } from './util';

export type SearchFormProps = {
  parentProductName: ProductFamilyName;
  closeModal: () => void;
};

const SmallSpinner = () => (
  <div css={spinnerStyles}>
    <Spinner size={20} />
  </div>
);

const SearchForm = ({ parentProductName, closeModal }: SearchFormProps) => {
  const [data, setData] = useState<marketplaceAddonDetails[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [search, setSearch] = useState('');
  const fetchPricing = useContext<IFetchPricing>(FetchPricingContext);
  const { searchMarketplaceAppsLabel, enterAppNameLabel, noMatchesLabel } =
    useModuleProps();

  const [selectedMarketplaceAddons] =
    useSelectedMarketplaceApps(parentProductName);
  const [, { updateMarketplaceApp }] = useGlobalStore();
  const { createAnalyticsEvent } = useAnalyticsEvents();

  const focusRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (focusRef.current) {
      focusRef.current.focus();
    }
  }, []);

  const fetchData = (search: string) => {
    if (search.length < 1) {
      setIsLoading(false);
      return Promise.resolve(setData([]));
    } else {
      return fetchPricing
        ?.ccpSearchAddons({ query: search, limit: 50 })
        .then((origAddons) => {
          return origAddons.filter((addon) => {
            //Check if addon has already been added
            return !Array.from(selectedMarketplaceAddons).find(
              (e) => e.name === addon.name
            );
          });
        })
        .then((addons) => {
          // Separate addons starting with search from the rest
          const filteredAddons = addons.filter((a) =>
            a.name.toLowerCase().startsWith(search.toLowerCase())
          );
          filteredAddons.sort((a, b) => a.name.localeCompare(b.name));
          const remainingAddons = addons.filter(
            (a) => !filteredAddons.includes(a)
          );

          // Sort the filtered addons alphabetically by name
          remainingAddons.sort((a, b) => a.name.localeCompare(b.name));

          // Concatenate the filtered addons and remaining addons
          const finalAddons = [
            ...filteredAddons,
            ...remainingAddons
          ] as marketplaceAddonDetails[];
          setData(finalAddons);
          setIsLoading(false);
        })
        .catch(console.error);
    }
  };

  const debouncedFetch = useCallback(debounce(fetchData, 500), []);

  useEffect(() => {
    if (search.length) {
      setIsLoading(true);
    }
    if (typeof debouncedFetch === 'function')
      debouncedFetch(search)?.catch(console.error);
  }, [search, debouncedFetch]);

  const updateAnalyticsEvent = (app: marketplaceAddonDetails) => {
    createAnalyticsEvent({
      action: 'click',
      component: 'button',
      eventName: ANALYTICS_EVENTS.selectApp,
      parentProductName,
      appName: app.name
    }).fire();
  };

  const handleKeyDown = (
    event: { key: string },
    app: marketplaceAddonDetails
  ) => {
    //Event.key ' ' is for spacebar
    if (event.key === 'Enter' || event.key === ' ') {
      updateMarketplaceApp(parentProductName, app);
      closeModal();
      updateAnalyticsEvent(app);
    }
  };

  const onAppClick = async (app: marketplaceAddonDetails) => {
    const offeringItems = await fetchPricing.getOfferingsByProductKey(
      app.ccpProductKey
    );
    app.offeringSku = extractOfferingSku(offeringItems);
    updateMarketplaceApp(parentProductName, app);
    closeModal();
    updateAnalyticsEvent(app);
  };

  return (
    <div css={searchFormContainerStyle}>
      <div css={searchFormTitleStyle}>{searchMarketplaceAppsLabel}</div>
      <div css={searchInputStyle}>
        <div
          css={searchBackButton}
          role="button"
          tabIndex={0}
          onClick={closeModal}
          onKeyDown={closeModal}
          aria-label={`Close search modal`}
        >
          <LeftIcon label="Edit" size="large" />
        </div>
        <TextInput
          ref={focusRef}
          id="marketplace-app-input"
          name="marketplace-app-input"
          label=""
          placeholder={enterAppNameLabel}
          Icon={!isLoading ? SearchIcon : SmallSpinner}
          onChangeHandler={(text) => setSearch(text)}
          autocomplete="off"
        />
      </div>
      {data.length > 0 && (
        <div css={searchResultContainerStyle}>
          {data.map((addonProduct, idx) => (
            <div
              key={addonProduct.name + '-' + idx}
              css={searchResultStyle}
              role="button"
              tabIndex={0}
              onClick={() => onAppClick(addonProduct)}
              onKeyDown={(event) => handleKeyDown(event, addonProduct)}
              aria-label={`Select ${addonProduct.name}`}
            >
              {addonProduct.name}
            </div>
          ))}
        </div>
      )}
      {search.length > 1 && !data.length && !isLoading && (
        <div css={searchNotFoundStyle}>{noMatchesLabel}</div>
      )}
    </div>
  );
};
export default SearchForm;
